import { ChangeEvent, useEffect, useState } from "react";
import { Alert, Box, Stack, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { CloudUploadOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";

import { ActionButton, EmptyState, FormDialog, InputField } from "../v3";
import { DocumentItem } from "./DocumentItem.component";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const DocumentsModal = ({
  open,
  onClose,
  documents,
  canUpload,
  canDelete,
  onUpload,
  onDelete,
  isUploading,
  deleteModal,
  onCloseDeleteModal,
  onConfirmDelete,
  isDeletingDocument,
}: any) => {
  const [file, setFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState("");
  const [wasUploading, setWasUploading] = useState(false);

  useEffect(() => {
    if (isUploading) {
      setWasUploading(true);
    } else if (wasUploading) {
      // Upload just completed, close the modal
      handleClose();
    }
  }, [isUploading]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.error("File size must be below 5MB");
        e.target.value = "";
        return;
      }
      setFile(selectedFile);
      if (!documentName) {
        setDocumentName(selectedFile.name.split(".").slice(0, -1).join("."));
      }
    }
  };

  const handleUpload = () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    onUpload?.(file);
  };

  const handleClose = () => {
    if (isUploading) return;
    setFile(null);
    setDocumentName("");
    setWasUploading(false);
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      onSubmit={handleUpload}
      title="All Documents"
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleClose} variant="outlined" disabled={isUploading} />
          {canUpload && <ActionButton buttonText="Upload" type="submit" loading={isUploading} disabled={!file} />}
        </>
      }
    >
      <Stack spacing={3} sx={{ py: 2 }}>
        {canUpload && (
          <>
            <InputField
              label="Document Name (Optional)"
              value={documentName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDocumentName(e.target.value)}
              fullWidth
              disabled={isUploading}
            />

            <Box
              sx={{
                border: "2px dashed #e0e0e0",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: isUploading ? "default" : "pointer",
                backgroundColor: "#fafafa",
                opacity: isUploading ? 0.6 : 1,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: isUploading ? "#fafafa" : "#f5f5f5",
                  borderColor: isUploading ? "#e0e0e0" : "#674BF5",
                },
              }}
              onClick={() => !isUploading && document.getElementById("crm-pro-file-upload")?.click()}
            >
              <input
                id="crm-pro-file-upload"
                type="file"
                hidden
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <CloudUploadOutlined sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
              <Typography variant="body1" color="textSecondary">
                {file ? file.name : "Click to select document"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Supported: PDF, DOC, DOCX, Images (Max 5MB)
              </Typography>
            </Box>
          </>
        )}

        {documents?.length ? (
          <Stack spacing={1}>
            {documents.map((doc) => (
              <DocumentItem key={doc.id} doc={doc} canDelete={canDelete} onDelete={onDelete} />
            ))}
          </Stack>
        ) : (
          <EmptyState title="No documents yet" subtitle="Upload a document to get started" />
        )}
      </Stack>

      <Dialog open={deleteModal?.isOpen} onClose={onCloseDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Document?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onCloseDeleteModal} variant="outlined" disabled={isDeletingDocument}>
            Cancel
          </Button>
          <Button onClick={onConfirmDelete} variant="contained" color="error" disabled={isDeletingDocument}>
            {isDeletingDocument ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </FormDialog>
  );
};
